import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container, Box, Typography, Button, CircularProgress, Paper, Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import PetsIcon from '@mui/icons-material/Pets';
import { db } from '../firebase';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';

const PublicBookingConfirmation = () => {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');
  const [acting, setActing] = useState(false);
  const [result, setResult] = useState(null); // 'confirmed' | 'cancelled' | null

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'onlineBookings'), where('bookingCode', '==', code))
        );
        if (snap.empty) {
          setError('No se encontró una reserva con ese código.');
          setLoading(false);
          return;
        }
        const data = snap.docs[0].data();
        setBooking({ id: snap.docs[0].id, ...data });
        setLoading(false);
      } catch (e) {
        setError('Error al cargar la reserva.');
        setLoading(false);
      }
    };
    load();
  }, [code]);

  const handleConfirm = async () => {
    if (!booking) return;
    setActing(true);
    try {
      await updateDoc(doc(db, 'onlineBookings', booking.id), {
        confirmationStatus: 'confirmed_by_patient',
        confirmedByPatientAt: new Date(),
        updatedAt: new Date(),
      });
      setResult('confirmed');
    } catch (e) {
      setError('No se pudo confirmar. Intenta nuevamente.');
    } finally {
      setActing(false);
    }
  };

  const handleCancel = async () => {
    if (!booking) return;
    setActing(true);
    try {
      await updateDoc(doc(db, 'onlineBookings', booking.id), {
        confirmationStatus: 'cancelled_by_patient',
        status: 'cancelled',
        cancelledAt: new Date(),
        updatedAt: new Date(),
      });
      setResult('cancelled');
    } catch (e) {
      setError('No se pudo cancelar. Intenta nuevamente.');
    } finally {
      setActing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    return `${d} ${meses[parseInt(m, 10) - 1]} ${y}`;
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Cargando reserva…</Typography>
      </Container>
    );
  }

  if (result === 'confirmed') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
          <Typography variant="h5" sx={{ mt: 2, fontWeight: 700 }}>¡Cita confirmada!</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Gracias por confirmar. Te esperamos el {formatDate(booking?.date)} a las {booking?.time}.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (result === 'cancelled') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <CancelIcon sx={{ fontSize: 64, color: 'error.main' }} />
          <Typography variant="h5" sx={{ mt: 2, fontWeight: 700 }}>Cita cancelada</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Tu cita ha sido cancelada. Si fue un error, vuelve a agendar en nuestro sitio.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error && !booking) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const alreadyConfirmed = booking?.confirmationStatus === 'confirmed_by_patient';
  const alreadyCancelled = booking?.status === 'cancelled' || booking?.confirmationStatus === 'cancelled_by_patient';

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <EventIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>Confirma tu cita</Typography>
          <Typography color="text.secondary">Código: {code}</Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {alreadyConfirmed && (
          <Alert severity="success" sx={{ mb: 2 }}>Ya confirmaste esta cita. Te esperamos.</Alert>
        )}
        {alreadyCancelled && (
          <Alert severity="info" sx={{ mb: 2 }}>Esta cita ya fue cancelada.</Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventIcon fontSize="small" color="action" />
            <Typography><strong>Fecha:</strong> {formatDate(booking?.date)} · {booking?.time}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography><strong>Tutor:</strong> {booking?.ownerName}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PetsIcon fontSize="small" color="action" />
            <Typography><strong>Mascota:</strong> {booking?.petName}</Typography>
          </Box>
          <Typography><strong>Profesional:</strong> {booking?.professionalName}</Typography>
          <Typography><strong>Especialidad:</strong> {booking?.specialtyName}</Typography>
        </Box>

        {!alreadyConfirmed && !alreadyCancelled && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="success"
              fullWidth
              startIcon={acting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
              onClick={handleConfirm}
              disabled={acting}
            >
              Confirmar cita
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={acting}
            >
              Cancelar
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default PublicBookingConfirmation;
